import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { FormField, FormSelect } from '../../components/FormField'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import { historyRow } from '../../components/historyRow'
import { apiErrorMessage } from '../../lib/errors'
import { businessDayOf, formatDate, formatDateTime, formatWholeDollars } from '../../lib/format'
import { toFormErrors, withoutErrorsFor, type FormErrors } from '../../lib/formErrors'
import { keepDecimal, parseAmount } from '../../lib/number'
import { NO_VIEW, type TableColumn } from '../../lib/tableView'
import { useTableView } from '../../lib/useTableView'
import {
  DOCUMENT_TYPES_BY_OWNER,
  INSURANCE_CERTIFICATE,
  INSURANCE_COVERAGES,
  REQUIRED_BY_OWNER,
  discardDocument,
  downloadDocument,
  listDocuments,
  uploadDocument,
  uploadInsuranceCertificate,
  type ComplianceDocument,
  type CoverageLineInput,
  type DocumentOwnerType,
  type InsuranceCoverageKey,
} from './api'
import { CLEARED_BY, FIELD_BY_CODE, documentMessageFor } from './errors'
import { paperName } from './names'

const noErrors: FormErrors = { fields: {}, summary: [] }

/** One row of the certificate grid, as typed. */
interface LineDraft {
  /** Whether the certificate carries this coverage at all — unticked lines are not sent. */
  listed: boolean
  policyNumber: string
  effectiveOn: string
  expiresOn: string
  limit: string
}

type LineCell = Exclude<keyof LineDraft, 'listed'>

const LINE_CELLS: readonly LineCell[] = ['policyNumber', 'effectiveOn', 'expiresOn', 'limit']

/** The grid at rest: the three required coverages ticked, the two optional ones not. */
function blankLines(): Record<InsuranceCoverageKey, LineDraft> {
  return Object.fromEntries(
    INSURANCE_COVERAGES.map(({ key, required }) => [
      key,
      { listed: required, policyNumber: '', effectiveOn: '', expiresOn: '', limit: '' },
    ]),
  ) as Record<InsuranceCoverageKey, LineDraft>
}

/**
 * One owner's paperwork: the papers on file with their standing against the calendar,
 * and the small form that adds the next one. Mounted on every owner's screen — the
 * carrier detail, the fleet edit forms, the broker edit — with only the owner changing.
 * A document is never edited: uploading the same type again supersedes the previous
 * paper, which stays visible as history, and a mistaken upload is discarded — its
 * bytes destroyed, its row struck from the panel.
 *
 * A carrier's insurance is filed from its ACORD 25 (TRKC-81): choosing the certificate
 * in the type select opens a grid of its coverage lines, and each line becomes its own
 * paper — its own policy number, dates, limit and standing — sharing the certificate's
 * file. Striking any line strikes the whole certificate.
 */
/** The standings a paper's column sorts in, and the three its filter offers. */
const STANDING_ORDER: readonly ComplianceDocument['standing'][] = ['Current', 'ExpiringSoon', 'Expired', 'NotYetEffective', 'NoExpiry']
const STANDING_CHOICES: readonly ComplianceDocument['standing'][] = ['Current', 'ExpiringSoon', 'Expired']
const STANDING_LABELS: Record<string, string> = {
  Current: 'documents.standing.current',
  ExpiringSoon: 'documents.standing.expiringSoon',
  Expired: 'documents.standing.expired',
}
const STATUSES: readonly ComplianceDocument['status'][] = ['Active', 'Superseded']

/** The papers in force first, then the ones superseded — whatever the column (TRKC-74). */
const inForceFirst = (paper: ComplianceDocument) => STATUSES.indexOf(paper.status)

/** What each column of a record's papers sorts and filters by (TRKC-110). */
function documentColumns(t: TFunction): TableColumn<ComplianceDocument>[] {
  const typeText = (paper: ComplianceDocument) => [t(`documents.types.${paper.type}`), paper.policyNumber ?? ''].join(' ')

  return [
    { key: 'type', sortValue: (paper) => t(`documents.types.${paper.type}`), filter: { kind: 'text', text: typeText } },
    { key: 'issuedOn', sortValue: (paper) => paper.issuedOn, filter: { kind: 'onOrAfter', value: (paper) => paper.issuedOn } },
    { key: 'expiresOn', sortValue: (paper) => paper.expiresOn, filter: { kind: 'onOrAfter', value: (paper) => paper.expiresOn } },
    {
      key: 'standing',
      sortValue: (paper) => STANDING_ORDER.indexOf(paper.standing),
      filter: { kind: 'choice', values: STANDING_CHOICES, matches: (paper, standing) => paper.standing === standing },
    },
    // Sorted by the instant, filtered by the day it shows — the yard's, never UTC's.
    {
      key: 'uploadedAt',
      sortValue: (paper) => paper.uploadedAt,
      filter: { kind: 'onOrAfter', value: (paper) => businessDayOf(paper.uploadedAt) },
    },
    { key: 'file', sortValue: (paper) => paper.fileName, filter: { kind: 'text', text: (paper) => paper.fileName } },
    {
      key: 'status',
      sortValue: inForceFirst,
      filter: { kind: 'choice', values: STATUSES, matches: (paper, status) => paper.status === status },
    },
  ]
}

/** The heading each column's filter box is named after. */
const DOCUMENT_HEADINGS: Record<string, string> = {
  type: 'documents.type',
  issuedOn: 'documents.issuedOn',
  expiresOn: 'documents.expiresOn',
  standing: 'documents.standingLabel',
  uploadedAt: 'documents.uploadedAt',
  file: 'documents.file',
  status: 'documents.statusLabel',
}

/** No papers yet: one array, so the view is not recomputed for an empty one every render. */
const NO_PAPERS: ComplianceDocument[] = []

export function DocumentsPanel({
  ownerType,
  ownerId,
  onOwnerStandingChanged,
}: {
  ownerType: DocumentOwnerType
  ownerId: string
  /**
   * Filing a paper or striking one re-judges the record that holds it, server-side
   * and immediately. The screen AROUND this panel is what shows that verdict, and it
   * has no way of knowing on its own — an end-to-end run caught exactly that: an
   * expired certificate uploaded, the carrier blocked in the database, and its badge
   * on the same screen still reading Pending until somebody reloaded.
   */
  onOwnerStandingChanged?: () => void
}) {
  const { t, i18n } = useTranslation()
  const availableTypes = DOCUMENT_TYPES_BY_OWNER[ownerType]

  const [documents, setDocuments] = useState<ComplianceDocument[] | null>(null)
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)

  // The papers sort and filter by their columns (TRKC-110), and start over for another record.
  const columns = useMemo(() => documentColumns(t), [t])
  const view = useTableView(documents ?? NO_PAPERS, columns, { rank: inForceFirst })
  const { replace: replaceView } = view
  useEffect(() => replaceView(NO_VIEW), [ownerType, ownerId, replaceView])
  // A failed download or discard: stored unresolved with the generic sentence that
  // fits what was attempted, so the API's own code is what gets translated.
  const [actionFailure, setActionFailure] = useState<
    { error: unknown; fallback: string } | null
  >(null)
  const [form, setForm] = useState({
    type: availableTypes[0]?.type ?? 'Other',
    issuedOn: '',
    expiresOn: '',
    notes: '',
  })
  const [lines, setLines] = useState<Record<InsuranceCoverageKey, LineDraft>>(blankLines)
  const [file, setFile] = useState<File | null>(null)
  // Bumped after each upload: a controlled value cannot clear a file input.
  const [fileInputKey, setFileInputKey] = useState(0)
  const [errors, setErrors] = useState<FormErrors>(noErrors)
  const [isUploading, setIsUploading] = useState(false)
  // Set when the server filed (or struck) the paper but could not re-judge the record
  // that holds it. Not an error: the paper is where it should be. Said anyway, because
  // the screen around this panel re-reads its badge on the strength of that judgement,
  // and a badge nobody re-judged looks exactly like one that was.
  const [standingUnconfirmed, setStandingUnconfirmed] = useState(false)

  const load = useCallback(async () => {
    try {
      setDocuments(await listDocuments(ownerType, ownerId))
      setLoadFailure(null)
    } catch (caught) {
      setDocuments(null)
      setLoadFailure({ error: caught })
    }
  }, [ownerType, ownerId])

  useEffect(() => {
    // A new owner: nothing said about the previous one may carry over. The reload
    // after a write calls load() directly and does not pass through here, so a
    // warning raised by that write survives its own reload.
    setStandingUnconfirmed(false)
    setActionFailure(null)
    void load()
  }, [load])

  const isCertificate = ownerType === 'Carrier' && form.type === INSURANCE_CERTIFICATE

  // Whether the scan may be left out: a medical card is identified by the driver's
  // name and CDL number, and the expiration is what the platform watches (TRKC-83).
  // The certificate always needs its file — the coverage lines share it.
  const fileOptional =
    !isCertificate &&
    (availableTypes.find((candidate) => candidate.type === form.type)?.fileOptional ?? false)

  function set(field: 'type' | 'issuedOn' | 'expiresOn' | 'notes', value: string) {
    setForm((current) => ({ ...current, [field]: value }))
    setErrors((current) => withoutErrorsFor(current, field, CLEARED_BY))
  }

  function setLine(key: InsuranceCoverageKey, cell: LineCell, value: string) {
    setLines((current) => ({ ...current, [key]: { ...current[key], [cell]: value } }))
    setErrors((current) => withoutErrorsFor(current, `${key}.${cell}`, CLEARED_BY))
  }

  function setListed(key: InsuranceCoverageKey, listed: boolean) {
    setLines((current) => ({ ...current, [key]: { ...current[key], listed } }))
    // A line the certificate does not carry has nothing to complain about.
    setErrors((current) =>
      LINE_CELLS.reduce((remaining, cell) => withoutErrorsFor(remaining, `${key}.${cell}`), current),
    )
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors(noErrors)

    if (file === null && !fileOptional) {
      setErrors({ fields: { file: 'document_file_empty' }, summary: [] })
      return
    }

    setIsUploading(true)
    try {
      let standingConfirmed: boolean

      if (isCertificate) {
        const coverages = certificateLines()
        if (coverages === null || file === null) {
          return
        }

        const filed = await uploadInsuranceCertificate({ ownerId, coverages, file })
        // One answer on every line: it is the carrier's standing, not the line's. Only
        // an explicit yes clears the warning — an answer that is missing is not one.
        standingConfirmed = filed.every((line) => line.ownerStandingConfirmed === true)
        setLines(blankLines())
      } else {
        const filed = await uploadDocument({
          ownerType,
          ownerId,
          type: form.type,
          issuedOn: blankToNull(form.issuedOn),
          expiresOn: blankToNull(form.expiresOn),
          notes: blankToNull(form.notes),
          file,
        })
        standingConfirmed = filed.ownerStandingConfirmed === true
        setForm((current) => ({ ...current, issuedOn: '', expiresOn: '', notes: '' }))
      }

      setFile(null)
      setFileInputKey((key) => key + 1)
      await load()
      // Replaced only by a completed answer: a refused upload confirms nothing, so a
      // warning left by a previous write stays until a write actually re-judges.
      setStandingUnconfirmed(!standingConfirmed)
      onOwnerStandingChanged?.()
    } catch (caught) {
      setErrors(toFormErrors(caught, FIELD_BY_CODE))
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * The ticked lines, ready to send — or null after pinning what cannot travel: a
   * limit that is not a number has no server-side shape (the wire carries numbers),
   * so it is the one complaint judged here, under the same code the server would use.
   */
  function certificateLines(): CoverageLineInput[] | null {
    const coverages: CoverageLineInput[] = []
    const fields: Record<string, string> = {}

    for (const { type, key } of INSURANCE_COVERAGES) {
      const line = lines[key]
      if (!line.listed) {
        continue
      }

      const limit = parseAmount(line.limit)
      if (line.limit.trim() !== '' && limit === null) {
        fields[`${key}.limit`] = `insurance_${key}_limit_invalid`
        continue
      }

      coverages.push({
        coverage: type,
        policyNumber: line.policyNumber.trim(),
        effectiveOn: line.effectiveOn,
        expiresOn: line.expiresOn,
        limit,
      })
    }

    if (Object.keys(fields).length > 0) {
      setErrors({ fields, summary: [] })
      return null
    }

    if (coverages.length === 0) {
      setErrors({ fields: {}, summary: ['insurance_certificate_empty'] })
      return null
    }

    return coverages
  }

  function handleDownload(id: string, fileName: string) {
    void downloadDocument({ id, fileName }).catch((caught: unknown) => {
      setActionFailure({ error: caught, fallback: 'documents.downloadError' })
    })
  }

  async function handleDiscard(row: ComplianceDocument) {
    // The one irreversible act on this screen earns the one confirmation on it —
    // and for a coverage line, the confirmation says how much goes with it. A paper
    // filed on its facts alone has no file to destroy, and the question says that too.
    const siblings =
      row.certificateId === null
        ? 0
        : (documents ?? []).filter((d) => d.certificateId === row.certificateId).length
    const question =
      siblings > 1
        ? t('documents.discardCertificateConfirm', { count: siblings })
        : row.fileName === null
          ? t('documents.discardConfirmNoFile')
          : t('documents.discardConfirm')

    if (!window.confirm(question)) {
      return
    }

    setActionFailure(null)

    try {
      const outcome = await discardDocument(row.id)
      await load()
      setStandingUnconfirmed(!outcome.standingConfirmed)
      onOwnerStandingChanged?.()
    } catch (caught) {
      // Kept unresolved, like every other failure here: the API's code (a losing
      // race answers document_already_discarded) is what gets translated, and it
      // re-translates when the language changes.
      setActionFailure({ error: caught, fallback: 'documents.discardError' })
    }
  }

  const field = (name: string) => {
    const code = errors.fields[name]
    return code === undefined ? undefined : documentMessageFor(code, t, 'documents.uploadError')
  }

  const expiryRequired =
    availableTypes.find((candidate) => candidate.type === form.type)?.expiryRequired ?? false

  // The required papers with nothing on file — a mirror of the rulebook's list, like
  // the type matrix above, for every kind of owner (TRKC-84). Said here, next to the
  // form that fixes it, because the badge on the screen around this panel says
  // "Paperwork incomplete" and not what is incomplete.
  const missingRequired =
    documents === null
      ? []
      : REQUIRED_BY_OWNER[ownerType].filter(
          (type) => !documents.some((d) => d.type === type && d.status === 'Active'),
        )

  // The paper that counts for an active line today: itself once its term has begun;
  // until then, the superseded paper the server resolved as covering it (TRKC-84) —
  // the server walked the chain of renewals, so nothing here reads a calendar or
  // follows a link, and what the panel says rests on what the evaluation judged.
  const inForceToday = (d: ComplianceDocument): ComplianceDocument | null => {
    if (d.standing !== 'NotYetEffective') {
      return d
    }

    return d.coveredByDocumentId === null
      ? null
      : (documents?.find((p) => p.id === d.coveredByDocumentId) ?? null)
  }

  // Filed, and still not in force: a renewal ahead of its effective date with nothing
  // covering today in its place — its predecessor lapsed, or there was none.
  const notYetInForce =
    documents === null
      ? []
      : documents.filter(
          (d) =>
            d.status === 'Active' &&
            d.standing === 'NotYetEffective' &&
            REQUIRED_BY_OWNER[ownerType].includes(d.type) &&
            inForceToday(d) === null,
        )

  // On file, current, and still not what a broker will load: the coverages in force
  // whose limit is under the minimum the server sent with them (Hugo's minimums,
  // TRKC-81). Judged on the paper that covers today — behind an early renewal that
  // is its predecessor, whose limit is the one the evaluation faced with the minimum.
  // The carrier reads Pending over these exactly as over a missing one.
  const underinsured =
    ownerType === 'Carrier' && documents !== null
      ? documents.flatMap((d) => {
          const judged = d.status === 'Active' ? inForceToday(d) : null
          return judged !== null &&
            judged.coverageLimit !== null &&
            judged.minimumLimit !== null &&
            judged.coverageLimit < judged.minimumLimit
            ? [{ type: d.type, limit: judged.coverageLimit, minimum: judged.minimumLimit }]
            : []
        })
      : []

  const coverageName = (type: string) => paperName(type, t)

  return (
    <section className="fleet documents">
      <header className="page__header--row fleet__header">
        <h2>{t('documents.title')}</h2>
      </header>

      {actionFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(actionFailure.error, t, actionFailure.fallback)}
        </p>
      )}

      {standingUnconfirmed && (
        <p className="alert alert--warning" role="status">
          {t('compliance.standingUnconfirmed')}
        </p>
      )}

      {loadFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'documents.loadError')}
        </p>
      )}

      {documents !== null && documents.length === 0 && (
        <p className="placeholder">{t('documents.empty')}</p>
      )}

      {documents !== null && documents.length > 0 && (
        <TableScroll label={t('documents.title')}>
          <table className="table">
            <caption className="visually-hidden">{t('documents.title')}</caption>
            <thead>
              <tr>
                {columns.map((column) => (
                  <SortableHeader key={column.key} view={view} column={column.key}>
                    {t(DOCUMENT_HEADINGS[column.key] ?? column.key)}
                  </SortableHeader>
                ))}
              </tr>
              <tr className="table__filters">
                {columns.map((column) => (
                  <FilterCell
                    key={column.key}
                    view={view}
                    column={column}
                    label={t(DOCUMENT_HEADINGS[column.key] ?? column.key)}
                    options={
                      column.key === 'standing'
                        ? STANDING_CHOICES.map((standing) => ({ value: standing, label: t(STANDING_LABELS[standing] ?? standing) }))
                        : column.key === 'status'
                          ? [
                              { value: 'Active', label: t('documents.active') },
                              { value: 'Superseded', label: t('documents.superseded') },
                            ]
                          : undefined
                    }
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {view.rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="placeholder">
                    {t('table.noneMatch')}
                  </td>
                </tr>
              )}
              {view.rows.map((row) => (
                <tr key={row.id} className={historyRow(row.status === 'Superseded')}>
                  <td>
                    {t(`documents.types.${row.type}`)}
                    {row.isCritical && (
                      <span className="documents__critical">{t('documents.critical')}</span>
                    )}
                    {/* A coverage line carries its policy, and its limit when the
                        certificate states one — the facts a broker asks about. */}
                    {row.policyNumber !== null && (
                      <span className="documents__policy">
                        {t('documents.policy', { number: row.policyNumber })}
                        {row.coverageLimit !== null &&
                          ` · ${formatWholeDollars(row.coverageLimit, i18n.language)}`}
                        {row.coverageLimit !== null &&
                          row.minimumLimit !== null &&
                          row.coverageLimit < row.minimumLimit && (
                            <span className="documents__below">
                              {t('documents.belowMinimum', {
                                minimum: formatWholeDollars(row.minimumLimit, i18n.language),
                              })}
                            </span>
                          )}
                      </span>
                    )}
                    {row.notes !== null && <NotesCell notes={row.notes} />}
                  </td>
                  <td className="table__numeric">
                    {row.issuedOn === null ? '—' : formatDate(row.issuedOn, i18n.language)}
                  </td>
                  <td className="table__numeric">
                    {row.expiresOn === null ? '—' : formatDate(row.expiresOn, i18n.language)}
                  </td>
                  <td>
                    <StandingBadge standing={row.standing} from={row.issuedOn} />
                  </td>
                  <td>{formatDateTime(row.uploadedAt, i18n.language)}</td>
                  <td>
                    {row.fileName === null ? (
                      // Filed on its facts alone: nothing to download, said plainly.
                      <span className="documents__nofile">{t('documents.noFile')}</span>
                    ) : (
                      <button
                        type="button"
                        className="documents__download"
                        onClick={() => handleDownload(row.id, row.fileName!)}
                      >
                        {row.fileName}
                      </button>
                    )}
                  </td>
                  <td>
                    {row.status === 'Superseded' ? t('documents.superseded') : t('documents.active')}
                    <button
                      type="button"
                      className="documents__discard"
                      onClick={() => void handleDiscard(row)}
                    >
                      {t('documents.discard')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      {documents !== null && documents.length > 0 && <FilterSummary view={view} />}

      {missingRequired.length > 0 && (
        <p className="documents__missing" role="status">
          {t('documents.missingCoverages', {
            list: missingRequired.map(coverageName).join(', '),
          })}
        </p>
      )}

      {notYetInForce.length > 0 && (
        <p className="documents__missing" role="status">
          {t('documents.notYetInForce', {
            list: notYetInForce
              .map(
                (d) =>
                  `${coverageName(d.type)} (${t('documents.standing.from', {
                    date: formatDate(d.issuedOn!, i18n.language),
                  })})`,
              )
              .join(', '),
          })}
        </p>
      )}

      {underinsured.length > 0 && (
        <p className="documents__missing" role="status">
          {t('documents.underinsuredCoverages', {
            list: underinsured
              .map(
                ({ type, limit, minimum }) =>
                  `${coverageName(type)} (${formatWholeDollars(limit, i18n.language)} / ${formatWholeDollars(minimum, i18n.language)})`,
              )
              .join(', '),
          })}
        </p>
      )}

      {errors.summary.length > 0 && (
        <div className="alert" role="alert">
          {errors.summary.map((code) => (
            <p key={code}>{documentMessageFor(code, t, 'documents.uploadError')}</p>
          ))}
        </div>
      )}

      <form className="form documents__form" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <FormSelect
          name="documentType"
          label={t('documents.type')}
          value={form.type}
          onChange={(value) => set('type', value)}
          options={availableTypes.map(({ type }) => ({
            value: type,
            label: t(`documents.types.${type}`),
          }))}
          error={field('type')}
          width="wide"
        />

        {isCertificate ? (
          <fieldset className="documents__certificate">
            <legend>{t('documents.certificate.title')}</legend>
            <p className="field__hint">{t('documents.certificate.hint')}</p>
            <TableScroll label={t('documents.certificate.title')} className="documents__grid-scroll">
              <table className="table documents__grid">
                <thead>
                  <tr>
                    <th scope="col">{t('documents.certificate.coverage')}</th>
                    <th scope="col">{t('documents.certificate.columns.policyNumber')}</th>
                    <th scope="col">{t('documents.certificate.columns.effectiveOn')}</th>
                    <th scope="col">{t('documents.certificate.columns.expiresOn')}</th>
                    <th scope="col">{t('documents.certificate.columns.limit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {INSURANCE_COVERAGES.map(({ key, required, minimum }) => {
                    const line = lines[key]
                    const coverage = t(`documents.coverages.${key}`)
                    const cellLabel = (cell: LineCell) =>
                      t('documents.certificate.cell', {
                        coverage,
                        field: t(`documents.certificate.fields.${cell}`),
                      })

                    return (
                      <tr key={key} className={line.listed ? undefined : 'documents__unlisted'}>
                        <td>
                          <label className="documents__coverage">
                            <input
                              type="checkbox"
                              checked={line.listed}
                              onChange={(event) => setListed(key, event.target.checked)}
                              aria-label={t('documents.certificate.listed', { coverage })}
                            />
                            {coverage}
                            {required && (
                              <span className="documents__critical">
                                {t('documents.certificate.required')}
                              </span>
                            )}
                          </label>
                        </td>
                        <td>
                          <GridCell
                            id={`coverage-${ownerId}-${key}-policyNumber`}
                            label={cellLabel('policyNumber')}
                            value={line.policyNumber}
                            onChange={(value) => setLine(key, 'policyNumber', value)}
                            error={field(`${key}.policyNumber`)}
                            disabled={!line.listed}
                            maxLength={50}
                          />
                        </td>
                        <td>
                          <GridCell
                            id={`coverage-${ownerId}-${key}-effectiveOn`}
                            label={cellLabel('effectiveOn')}
                            value={line.effectiveOn}
                            onChange={(value) => setLine(key, 'effectiveOn', value)}
                            error={field(`${key}.effectiveOn`)}
                            disabled={!line.listed}
                            type="date"
                          />
                        </td>
                        <td>
                          <GridCell
                            id={`coverage-${ownerId}-${key}-expiresOn`}
                            label={cellLabel('expiresOn')}
                            value={line.expiresOn}
                            onChange={(value) => setLine(key, 'expiresOn', value)}
                            error={field(`${key}.expiresOn`)}
                            disabled={!line.listed}
                            type="date"
                          />
                        </td>
                        <td>
                          <GridCell
                            id={`coverage-${ownerId}-${key}-limit`}
                            label={cellLabel('limit')}
                            value={line.limit}
                            onChange={(value) => setLine(key, 'limit', keepDecimal(line.limit, value))}
                            // The floor a broker asks for, as a hint in the empty box.
                            placeholder={
                              minimum === null
                                ? undefined
                                : t('documents.certificate.minimum', {
                                    amount: formatWholeDollars(minimum, i18n.language),
                                  })
                            }
                            error={field(`${key}.limit`)}
                            disabled={!line.listed}
                            inputMode="decimal"
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </TableScroll>
          </fieldset>
        ) : (
          <>
            <FormField
              name="issuedOn"
              label={t('documents.issuedOn')}
              value={form.issuedOn}
              onChange={(value) => set('issuedOn', value)}
              error={field('issuedOn')}
              type="date"
            />
            <FormField
              name="expiresOn"
              label={t('documents.expiresOn')}
              value={form.expiresOn}
              onChange={(value) => set('expiresOn', value)}
              error={field('expiresOn')}
              type="date"
              required={expiryRequired}
            />
            <FormField
              name="documentNotes"
              label={t('documents.notes')}
              value={form.notes}
              onChange={(value) => set('notes', value)}
              error={field('notes')}
              maxLength={500}
              multiline
              width="wide"
            />
          </>
        )}
        {/* Two deliberate rows: the paper's facts above, the file and the action
            below — six controls on one line truncated the file input. */}
        <div className="form__break" aria-hidden="true" />
        <div className="field">
          <label htmlFor={`file-${ownerId}`}>{t('documents.file')}</label>
          <input
            key={fileInputKey}
            id={`file-${ownerId}`}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null)
              setErrors((current) => withoutErrorsFor(current, 'file', CLEARED_BY))
            }}
            aria-invalid={field('file') !== undefined}
            // The hint travels with the field: a screen reader announces the accepted
            // formats and the size cap on focus, not only sighted readers.
            aria-describedby={
              field('file') === undefined
                ? `file-${ownerId}-hint`
                : `file-${ownerId}-hint file-${ownerId}-error`
            }
          />
          <span className="field__hint" id={`file-${ownerId}-hint`}>
            {fileOptional ? t('documents.fileOptionalHint') : t('documents.fileHint')}
          </span>
          {field('file') !== undefined && (
            <span className="field__error" id={`file-${ownerId}-error`} role="alert">
              {field('file')}
            </span>
          )}
        </div>
        <div className="form__actions">
          <button type="submit" className="button" disabled={isUploading}>
            {isUploading ? t('documents.saving') : t('documents.save')}
          </button>
        </div>
      </form>
    </section>
  )
}

/**
 * One cell of the certificate grid: an input whose label is spoken, not shown — the
 * column header and the coverage name carry it for sighted readers — with the room
 * its complaint occupies underneath, tied by id like every FormField.
 */
function GridCell({
  id,
  label,
  value,
  onChange,
  error,
  disabled,
  type = 'text',
  maxLength,
  inputMode,
  placeholder,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  disabled: boolean
  type?: string
  maxLength?: number
  inputMode?: 'decimal' | 'numeric'
  placeholder?: string
}) {
  const errorId = `${id}-error`

  return (
    <div className="documents__cell">
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        aria-label={label}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? undefined : errorId}
      />
      {error !== undefined && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

/** A long margin note would swallow its row; past this length it folds. */
const NOTES_PREVIEW_LENGTH = 120

/**
 * The note truncated in place, expanding in place — deliberately not a tooltip or a
 * floating panel: hover does not exist on touch, popovers need positioning
 * machinery, and unfolding inside the cell keeps the reading context.
 */
function NotesCell({ notes }: { notes: string }) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const needsToggle = notes.length > NOTES_PREVIEW_LENGTH

  return (
    <span className="documents__notes">
      {isOpen || !needsToggle ? notes : `${notes.slice(0, NOTES_PREVIEW_LENGTH)}…`}
      {needsToggle && (
        <button
          type="button"
          className="documents__notes-toggle"
          onClick={() => setIsOpen((open) => !open)}
        >
          {isOpen ? t('documents.notesLess') : t('documents.notesMore')}
        </button>
      )}
    </span>
  )
}

function StandingBadge({
  standing,
  from,
}: {
  standing: ComplianceDocument['standing']
  /** The day the term begins — what a not-yet-effective badge names (TRKC-84). */
  from: string | null
}) {
  const { t, i18n } = useTranslation()

  if (standing === 'NoExpiry') {
    return <span>—</span>
  }

  if (standing === 'NotYetEffective') {
    return (
      <span className="badge badge--pending">
        {t('documents.standing.from', { date: from === null ? '—' : formatDate(from, i18n.language) })}
      </span>
    )
  }

  const variant =
    standing === 'Current' ? 'badge--active' : standing === 'ExpiringSoon' ? 'badge--pending' : 'badge--blocked'
  const key =
    standing === 'Current' ? 'current' : standing === 'ExpiringSoon' ? 'expiringSoon' : 'expired'

  return <span className={`badge ${variant}`}>{t(`documents.standing.${key}`)}</span>
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
